import React from 'react';
import styled from 'styled-components';

import { easing, EASE } from '../../ui/easing';
import colors from '../../ui/colors';
import Container from '../../ui/Container';
import { media } from '../../ui/utils';

const TRANSITION_DURATION = 350;

const MenuButton = styled.button`
  height: 1.5rem;
  width: 1.5rem;
  background: transparent;
  margin: 0 0 0 1.375rem;
  padding: 0;
  color: ${colors.BRAND_GREEN};
  

  svg {
    width: 1.25rem;
    height: 1.25rem;
    fill: ${colors.BRAND_GREEN};
  }

  &:first-of-type {
    margin: 0 1.375rem 0 0;
    ${props => props.video && `
      transition: opacity .1s;
      cursor: pointer;
      margin: 0;
      &:hover{
        opacity: .8;
      }
      svg{
        fill: ${colors.WHITE};
      `}
  }
`;

const ContentContainer = styled(Container)`
  -webkit-overflow-scrolling: touch;
  -ms-overflow-style: -ms-autohiding-scrollbar;
  overflow-y: scroll;

  display: flex;
  flex-direction: column;

  > *:first-child {
    flex: 1 0;
  }
  ${props =>
    props.video &&
    `
    position: relative;
    top: 0 !important;
  `}
`;

const ModalHeader = styled.header`
  height: 3.75rem;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  border-bottom: 1px solid ${colors.GRAY_LIGHT};
  //position: fixed;
  position: absolute;
  background: white;
  width: 100%;
  top: 0;
  z-index: 0;
  justify-content: space-between;
  box-sizing: border-box;
  line-height: 0;

  color: ${colors.BRAND_GREEN};

  ${props =>
    props.video &&
    `
    background: transparent;
    justify-content: flex-end;
    position: relative;
    border: none;
  `}

  h2 {
    font-size: 1.5rem;
    text-align: center;
    text-transform: uppercase;
    flex: 1;
    font-weight: 400;
  }
`;

const ModalContainerBase = styled(({children, zIndex, first=false, ...rest}) => {
  return (
    <div {...rest}>
      <div className="modal-container">
        {children}
      </div>
    </div>
  )
})`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: ${props => props.zIndex ? props.zIndex : 1};

  &.modal-appear, &.modal-enter {
    .modal-container {
      transform: translate3d(0, 100%, 0);
    }
    &.modal-appear-active, &.modal-enter-active {
      .modal-container {
        transform: translate3d(0, 0%, 0);
        transition-property: transform;
        transition-duration: ${TRANSITION_DURATION}ms;
      }
    }
  }

  &.modal-leave {
    .modal-container {
      transform: translate3d(0, 0%, 0);
      z-index: 1;
    }
    &.modal-leave-active {
      .modal-container {
        transform: translate3d(0, 100%, 0);
        transition-property: transform;
        transition-duration: ${TRANSITION_DURATION}ms;
      }
    }
  }


  &.slide-pop-enter {
    .modal-container {
      transform: translate3d(-20%, 0, 0);
      z-index: 0;
    }
    &.slide-pop-enter-active {
      .modal-container {
        transform: translate3d(0, 0, 0);
        transition-property: transform;
        transition-duration: ${TRANSITION_DURATION}ms;
      }
    }
  }

  &.slide-pop-leave {
    .modal-container {
      transform: translate3d(0, 0, 0);
      z-index: 1;
    }
    &.slide-pop-leave-active {
      .modal-container {
        transform: translate3d(100%, 0, 0);
        transition-property: transform;
        transition-duration: ${TRANSITION_DURATION}ms;
      }
    }
  }

  &.slide-push-enter {
    transform: translate3d(100%, 0, 0);
    z-index: 1;
    &.slide-push-enter-active {
      transform: translate3d(0, 0, 0);
      transition-property: transform;
      transition-duration: ${TRANSITION_DURATION}ms;
    }
  }

  &.slide-push-leave {
    transform: translate3d(0, 0, 0);
    z-index: 0;
    &.slide-push-leave-active {
      transform: translate3d(-20%, 0, 0);
      transition-property: transform;
      transition-duration: ${TRANSITION_DURATION}ms;
    }
  }
`;

const ModalContainer = styled(ModalContainerBase)`
  .modal-container {
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: ${props => props.zIndex ? props.zIndex : 1};
    position: fixed;
    background: white;

    ${easing(EASE.easeInOutCirc)};
    transform: translate3d(0, 0, 0);

    > div {
      padding: 1rem 1rem;
      box-sizing: border-box;
    }
  }
  
  ${media.tabletWide`
    .modal-container {
      position: relative;
      overflow: hidden;
      max-height: 800px;
      min-height: 500px;
      max-width: 480px;
      min-width: 320px;
      width: 100%;
      height: 100%;

      > div {
        top: 3.75rem;
      }
    }
    ${props => props.first && `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fff;
      z-index: 10000;
      ${easing(EASE.easeInOutCirc)};


      &.modal-appear, &.modal-enter {
        opacity: 0;
        .modal-container {
          transform: translate3d(0, 10%, 0);
        }
        &.modal-appear-active, &.modal-enter-active {
          opacity: 1;
          transition-property: opacity;
          transition-duration: ${TRANSITION_DURATION}ms;
        }
      }

      &.modal-leave {
        opacity: 1;
        &.modal-leave-active {
          opacity: 0;
          transition-property: opacity;
          transition-duration: ${TRANSITION_DURATION}ms;
          .modal-container {
            transform: translate3d(0, 10%, 0);
          }
        }
      }
      
      > .modal-container {
        width: 90%;
        height: 90%;
      }
    `}
  `}
`;

const NotificationContainer = styled(ModalContainerBase)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  z-index: 10000;
  
  .modal-container {
    margin: 0 2rem;
    background: white;
    position: relative;
    overflow: hidden;
    max-width: 600px;
    display: flex;
    flex-direction: column;
    box-sizing: border-box;
    padding: ${29/16}rem ${39/16}rem ${32/16}rem ${34/16}rem;

    > div {
      top: 3.75rem;
    }
  }
  
  &.modal-appear, &.modal-enter {
    opacity: 0;
    .modal-container {
      transform: translate3d(0, 10%, 0);
    }
    &.modal-appear-active, &.modal-enter-active {
      opacity: 1;
      transition-property: opacity;
      transition-duration: ${TRANSITION_DURATION}ms;
    }
  }

  &.modal-leave {
    opacity: 1;
    &.modal-leave-active {
      opacity: 0;
      transition-property: opacity;
      transition-duration: ${TRANSITION_DURATION}ms;
      .modal-container {
        transform: translate3d(0, 10%, 0);
      }
    }
  }
`;

const NotificationHeader = styled.div`
  text-align: left;
  display: flex;
  margin-bottom: 1rem;
  
  a {
    cursor: pointer;
  
    &:hover {
      svg {
        fill: rgba(74, 74, 74, .9);
      }
    }
  }
`;

const NotificationContent = styled.div`
  flex: 1;
  text-align: left;
  
  p {
    font-family: 'Roboto', sans-serif;
    font-size: ${17/16}rem;
    color: #6F6F6F;
    letter-spacing: 0;
    line-height: 28px;
    margin-bottom: 1rem;
    
    a {
      cursor: pointer;
      color: ${colors.FIELD_ERROR};
      
      &:hover {
        color: rgba(237, 85, 100, .90);
      }
    }
    
    &:last-child {
      margin-bottom: 0;
    }
    ${media.notTabletWide`
      font-size: 1rem;
      line-height: ${26 / 16}rem;
    `}
  }
`;

const NotificationTitle = styled.span`
  font-weight: bold;
  font-size: ${28/16}rem;
  color: #4A4A4A;
  letter-spacing: 0;
  line-height: ${30/16}rem;
  flex: 1;
  ${media.notTabletWide`
    font-size: ${20 / 16}rem;
    line-height: ${22/16}rem;
  `}
`;

const VideoContainer = styled(ModalContainerBase)`
  
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  z-index: 10000;

  .modal-container {
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    z-index: ${props => props.zIndex ? props.zIndex : 1};
    position: relative;
    overflow: hidden;
    ${easing(EASE.easeOut)};
    transform: translate3d(0, 0, 0);

    > div {
      box-sizing: border-box;
    }
    width: 90%;
    height: auto;
  }

  &.modal-appear, &.modal-enter {
    opacity: 0;
    .modal-container {
      transform: translate3d(0, 10%, 0);
    }
    &.modal-appear-active, &.modal-enter-active {
      opacity: 1;
      transition-property: opacity;
      transition-duration: ${TRANSITION_DURATION}ms;
    }
  }

  &.modal-leave {
    opacity: 1;
    &.modal-leave-active {
      opacity: 0;
      transition-property: opacity;
      transition-duration: ${TRANSITION_DURATION}ms;
      .modal-container {
        transform: translate3d(0, 10%, 0);
      }
    }
  }
  
`;

/*
const ModalContainer = styled.div`
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: ${props => props.zIndex ? props.zIndex : 1};
  position: fixed;
  background: white;

  ${easing(EASE.easeInOutCirc)};

  &.modal-appear, &.modal-enter {
    transform: translate3d(0, 100%, 0);
    &.modal-appear-active, &.modal-enter-active {
      transform: translate3d(0, 0%, 0);
      transition-property: transform;
      transition-duration: ${TRANSITION_DURATION}ms;
    }
  }

  &.modal-leave {
    transform: translate3d(0, 0%, 0);
    z-index: 1;
    &.modal-leave-active {
      transform: translate3d(0, 100%, 0);
      transition-property: transform;
      transition-duration: ${TRANSITION_DURATION}ms;
    }
  }


  &.slide-pop-enter {
    transform: translate3d(-20%, 0, 0);
    z-index: 0;
    &.slide-pop-enter-active {
      transform: translate3d(0, 0, 0);
      transition-property: transform;
      transition-duration: ${TRANSITION_DURATION}ms;
    }
  }

  &.slide-pop-leave {
    transform: translate3d(0, 0, 0);
    z-index: 1;
    &.slide-pop-leave-active {
      transform: translate3d(100%, 0, 0);
      transition-property: transform;
      transition-duration: ${TRANSITION_DURATION}ms;
    }
  }


  &.slide-push-enter {
    transform: translate3d(100%, 0, 0);
    z-index: 1;
    &.slide-push-enter-active {
      transform: translate3d(0, 0, 0);
      transition-property: transform;
      transition-duration: ${TRANSITION_DURATION}ms;
    }
  }

  &.slide-push-leave {
    transform: translate3d(0, 0, 0);
    z-index: 0;
    &.slide-push-leave-active {
      transform: translate3d(-20%, 0, 0);
      transition-property: transform;
      transition-duration: ${TRANSITION_DURATION}ms;
    }
  }
`;
*/
export {
  TRANSITION_DURATION,
  ModalContainer,
  ModalHeader,
  MenuButton,
  ContentContainer,
  NotificationContainer,
  NotificationHeader,
  NotificationContent,
  NotificationTitle,
  VideoContainer,
}



// WEBPACK FOOTER //
// ./src/modules/common/components/modal/ui/index.js